import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, MessageCircle, Volume2, Zap, Trophy, Flame, Award, Calendar, Target, ChevronRight, ChevronLeft } from 'lucide-react';
import { getT } from './i18n';

/**
 * Guide onboarding — comment utiliser JoSmartTech Academy.
 * Auto-affiché à la 1ère visite (flag localStorage) + bouton "Guide" permanent dans le header.
 * Bilingue FR / EN / LN.
 */

const STORAGE_KEY = 'academy_guide_seen_v1';

export function shouldShowGuide() {
  return typeof window !== 'undefined' && !localStorage.getItem(STORAGE_KEY);
}

export function markGuideSeen() {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* ignore */ }
}

const STEPS = [
  { icon: BookOpen,     key: 'step_method' },
  { icon: Zap,          key: 'step_dailychallenge' },
  { icon: Trophy,       key: 'step_gamification' },
  { icon: Calendar,     key: 'step_streak' },
  { icon: Award,        key: 'step_certificates' },
  { icon: Target,       key: 'step_goal' },
];

export default function GettingStartedGuide({ lang = 'fr', onClose }) {
  const t = getT(lang);
  const [idx, setIdx] = useState(0);

  // Empêcher scroll de la page pendant l'ouverture
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const step = STEPS[idx];
  const Icon = step.icon;
  const isLast = idx === STEPS.length - 1;

  const close = () => {
    markGuideSeen();
    onClose && onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="guide-overlay"
        onClick={close}
      >
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.4 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-emerald-500/10"
          onClick={(e) => e.stopPropagation()}
          data-testid="guide-modal"
        >
          <button onClick={close} data-testid="guide-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/60 hover:text-white transition">
            <X size={16} />
          </button>

          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">{t('guide_eyebrow')}</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-1 bg-gradient-to-r from-emerald-300 to-yellow-300 bg-clip-text text-transparent">
            {t('guide_title')}
          </h2>
          <p className="text-xs text-white/60 mb-6">{t('guide_sub')}</p>

          {/* Progress bar */}
          <div className="flex gap-1.5 mb-6" data-testid="guide-progress">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`flex-1 h-1 rounded-full transition ${i <= idx ? 'bg-emerald-400' : 'bg-white/10'}`} />
            ))}
          </div>

          <motion.div key={idx} initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.3 }}
            className="min-h-[220px]">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-yellow-500/20 border border-emerald-400/40 flex items-center justify-center mb-4">
              <Icon size={26} className="text-emerald-300" />
            </div>
            <h3 className="text-lg font-bold mb-2">{t(`${step.key}_title`)}</h3>
            <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{t(`${step.key}_desc`)}</p>
          </motion.div>

          <div className="flex items-center justify-between mt-6 gap-3">
            <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} data-testid="guide-prev"
              className="text-xs text-white/50 hover:text-white disabled:opacity-30 flex items-center gap-1">
              <ChevronLeft size={14} />{t('guide_prev')}
            </button>
            <div className="text-[11px] text-white/40">{idx + 1} / {STEPS.length}</div>
            {isLast ? (
              <button onClick={close} data-testid="guide-finish"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1">
                {t('guide_finish')}
              </button>
            ) : (
              <button onClick={() => setIdx(i => Math.min(STEPS.length - 1, i + 1))} data-testid="guide-next"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1">
                {t('guide_next')}<ChevronRight size={14} />
              </button>
            )}
          </div>

          <button onClick={close} data-testid="guide-skip"
            className="mt-3 w-full text-[11px] text-white/40 hover:text-white/70 transition">
            {t('guide_skip')}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
